/**
 * Browser Notifications Service
 * Request permission and send notifications when generation is complete
 */

const NOTIFICATION_PERMISSION_KEY = 'emd_notifications_enabled'

/**
 * Check if notifications are supported
 */
export function isNotificationSupported() {
  return 'Notification' in window
}

/**
 * Get current notification permission status
 */
export function getNotificationPermission() {
  if (!isNotificationSupported()) return 'unsupported'
  return Notification.permission // 'granted', 'denied', 'default'
}

/**
 * Check if user has enabled notifications in our app
 */
export function isNotificationEnabled() {
  return localStorage.getItem(NOTIFICATION_PERMISSION_KEY) === 'true'
}

/**
 * Request notification permission from user
 */
export async function requestNotificationPermission() {
  if (!isNotificationSupported()) {
    return { success: false, reason: 'unsupported' }
  }

  if (Notification.permission === 'granted') {
    localStorage.setItem(NOTIFICATION_PERMISSION_KEY, 'true')
    return { success: true, permission: 'granted' }
  }

  if (Notification.permission === 'denied') {
    return { success: false, reason: 'denied' }
  }

  // Request permission
  try {
    const permission = await Notification.requestPermission()

    if (permission === 'granted') {
      localStorage.setItem(NOTIFICATION_PERMISSION_KEY, 'true')
      return { success: true, permission: 'granted' }
    }

    return { success: false, reason: permission }
  } catch (error) {
    console.error('Notification permission error:', error)
    return { success: false, reason: 'error' }
  }
}

/**
 * Disable notifications
 */
export function disableNotifications() {
  localStorage.setItem(NOTIFICATION_PERMISSION_KEY, 'false')
}

/**
 * Send a notification
 */
export function sendNotification(title, options = {}) {
  if (!isNotificationSupported()) return null
  if (Notification.permission !== 'granted') return null
  if (!isNotificationEnabled()) return null

  const defaultOptions = {
    icon: '/favicon.svg',
    badge: '/favicon.svg',
    tag: 'emd-factory',
    renotify: true,
    requireInteraction: false,
    ...options,
  }

  try {
    const notification = new Notification(title, defaultOptions)

    // Focus window when notification is clicked
    notification.onclick = () => {
      window.focus()
      notification.close()
      if (options.onClick) options.onClick()
    }

    // Auto-close after 10 seconds
    setTimeout(() => notification.close(), 10000)

    return notification
  } catch (error) {
    console.error('Send notification error:', error)
    return null
  }
}

/**
 * Notify when site generation is complete
 */
export function notifyGenerationComplete(domain, pageCount) {
  return sendNotification('Site généré !', {
    body: `${domain} - ${pageCount} pages créées. Cliquez pour voir le résultat.`,
    tag: 'generation-complete',
    requireInteraction: true,
  })
}

/**
 * Notify when a phase is complete
 */
export function notifyPhaseComplete(phaseName) {
  return sendNotification(`Phase terminée : ${phaseName}`, {
    body: 'La génération continue...',
    tag: 'phase-complete',
    silent: true,
  })
}

/**
 * Notify on error
 */
export function notifyError(message) {
  return sendNotification('Erreur EMD Factory', {
    body: message,
    tag: 'error',
    requireInteraction: true,
  })
}

export default {
  isNotificationSupported,
  getNotificationPermission,
  isNotificationEnabled,
  requestNotificationPermission,
  disableNotifications,
  sendNotification,
  notifyGenerationComplete,
  notifyPhaseComplete,
  notifyError,
}
